import React, { useState, useEffect } from "react";
import { API_URL } from "../../utils/Constant";
import ChartComponent from "../../components/chart/ChartComponent";
import { ProgressBar } from "../../components/progressBar/ProgressBar"
import { Select, DatePicker } from "antd";
import { capitalizeAndRemoveUnderscores, ctrlDcmalVal, normalizeTeamProductivityData } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const { Option } = Select;

const { RangePicker } = DatePicker;
const tailwindColors = [
  "#7dd3fc", // sky-300
  "#fdba74", // orange-300
  "#5eead4", // teal-300
  "#fde68a", // yellow-300
  "#fca5a5"  // red-300
];
const NatureOfPaymentType = [
  { value: "all", label: "All" },
  { value: "customer", label: "Customer" },
  { value: "vendor", label: "Vendor" },
  { value: "partnership", label: "Partnership" },
  { value: "other", label: "Other" },
];

function CompanyAnalytics() {
  const select = useSelector((state) => state.auth.userInfo);
  const role = select.role;
  const currency = select.currency;
  const [top10Customers, setTop10Customers] = useState([]);
  const [agreementCount, setAgreementCount] = useState([]);
  const [teamProductivityData, setTeamProductivityData] = useState([]);
  const [teamProductivityDataId, setTeamProductivityDataId] = useState("");
  const [paymentFrequency, setPaymentFrequency] = useState([]);
  const [productWiseSummary, setProductWiseSummary] = useState([]);
  const [completed, setCompleted] = useState(0);
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeUserIndex, setActiveUserIndex] = useState(null);

  const handlePointMouseEnter = (seriesIndex) => {
    setActiveUserIndex(seriesIndex);
  };

  const handleMouseLeave = () => {
    setActiveUserIndex(null);
  };


  const fetchProductWiseSummary = async (selectedProductName = null) => {
    try {
      let url = "/chart/get/product-wise-agreement-count/";
      const response = await axiosInstance.get(`${url}`);
      setProductWiseSummary(response.data.data); // Set the data to state
    } catch (error) {
      console.error("Error fetching product-wise summary:", error);
    }
  };

  const productNames = productWiseSummary?.map((item) => item?.product_name ?? "Product1");

  const productCount = productWiseSummary?.map(item => Math.round(item?.count ?? 0));

  const maxProductValue = Math.max(...productCount);
  const niceStep = 1;
  const xaxisMax = Math.ceil((maxProductValue + 2) / niceStep) * niceStep;
  const tickAmount = xaxisMax + 1;
  const productWiseData = {
    series: [
      {
        name: "Contracts",
        data: productCount,
      },
    ],
    options: {
      chart: {
        type: "bar",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1000,
          animateGradually: {
            enabled: true,
            delay: 200,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 800,
          },
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
          },
          export: {
            csv: { filename: "Product-Summary" },
            svg: { filename: "Product-Chart", width: 20000, height: 10000, },
            png: { filename: "Product-Chart" },
          },
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const id = productWiseSummary[config.dataPointIndex]?.contract_identifier;
            const product_name = productWiseSummary[config.dataPointIndex]?.product_name;

            if (product_name) {
              setTimeout(() => {
                navigate(`/admin/contracts?id=${id}`, {
                  state: { product_name, status: "EXECUTED" },
                });
              }, 100);
            }
          },
        },
      },

      title: {
        text: "Product Summary",
        style: {
          fontSize: "16px",
          fontWeight: "bold",
          color: "#263238",
        },
      },

      xaxis: {
        categories: productNames,
        min: 0,
        max: xaxisMax, // ✅ extend the axis
        tickAmount: tickAmount,
        forceNiceScale: true,
        decimalsInFloat: 0,
        labels: {
          formatter: function (val) {
            return Math.round(val); // ✅ Make X-axis labels clean
          },
          style: {
            fontSize: "13px",
            fontWeight: "bold",
            colors: "#334155",
          },
        },
      },
      legend: {
        fontSize: "13px",
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "10px",
            lineHeight: "12px",
            colors: "#334155",
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return Math.round(val);
          },
        },
      },
      colors: tailwindColors.slice(0, productCount.length),

      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "80%",
          distributed: true,
        },
      },

      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "bold",
          colors: ["#263238"],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.2,
        },
        formatter: function (val) {
          return Math.round(val);
        },
      },
    },
  };


  const fetchTop10Customers = async (value) => {
    try {
      const response = await axiosInstance.post(`/chart/get/top-ten-customer/`, {
        nature: value ? value : "customer"
      });
      setTop10Customers(response.data.data);
      // window.location.reload(); // Update the state with the fetched company data
    } catch (error) { }
  };

  const fetchAgreementCount = async (startDate, endDate) => {
    try {
      const response = await axiosInstance.get(`/chart/get/agreement-count/`, {
        customParams: {
          start_date: startDate ?? startDate,
          end_date: endDate ?? endDate,
        }
      });
      setAgreementCount(response.data);
      // window.location.reload(); // Update the state with the fetched company data
    } catch (error) { }
  };

  const statusNames =
    agreementCount &&
    agreementCount.map((item) => capitalizeAndRemoveUnderscores(item.status));
  const statusCount =
    agreementCount && agreementCount.map((item) => item.count);

  const maxCountValue = Math.max(...statusCount);

  const yaxisCountOptions = {
    max:
      maxCountValue < 10 ? 10 : Math.round(maxCountValue + maxCountValue / 2),
  };
  const statusUrlMapping = {
    "Fresh Request": "/admin/contracts/agreement_type/fresh_request/",
    "Under Execution": "/admin/contracts/agreement_type/under_execution/",
    "Executed": "/admin/contracts/agreement_type/executed/",
    "Under Negotiation": "/admin/contracts/agreement_type/under_negotiation/",
  };


  const agreementCountData = {
    series: statusCount,
    options: {
      chart: {
        type: "pie",
        animations: {
          enabled: true,
          easing: "easeinout", // ✅ smooth in/out
          speed: 1000,
          animateGradually: {
            enabled: true,
            delay: 200,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 800,
          },
        },
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
          },
          export: {
            csv: {
              filename: "Status-Wise-Summary", // ✅ CSV file name
            },
            svg: {
              filename: "Status-Wise-Chart", // ✅ SVG file name
            },
            png: {
              filename: "Status-Wise-Chart", // ✅ PNG file name
            },
          },
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const selectedStatus = statusNames[config.dataPointIndex]; // Get the status name
            const url = statusUrlMapping[selectedStatus]; // Get the corresponding URL
            if (url) {
              setTimeout(() => navigate(url), 100);
            }
          },
        },
      },
      labels: statusNames,
      colors: tailwindColors.slice(0, statusCount.length),
      title: {
        text: "Status Wise Summary",
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#263238'
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "bold",
          colors: ["#263238"],
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          },
        },
        formatter: function (val, opts) {
          const count = agreementCountData.series[opts.seriesIndex];
          return count;
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.2,
        },
      },
      stroke: {
        show: true,
        colors: ['#fff'],
        width: 3,
      },
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          expandOnClick: true,
          donut: {
            size: "80%",
            labels: {
              show: false,
            },
          },
        },
      },
      legend: {
        position: "bottom",
        fontSize: "13px",
        horizontalAign: "center",
      },
      xaxis: {
        categories: statusNames,
      },
      yaxis: yaxisCountOptions,
    },
  };

  const handleSearchTeamProductivity = async (inputText) => {
    if (!inputText) return;
    setSearching(true);
    try {
      const payload = { document_id: inputText };
      const res = await axiosInstance.post(`/matching-documents/`, payload);
      const data = res.data.document_ids;
      const formatted = data.map((item) => ({
        label: item,
        value: item,
      }));
      setOptions(formatted);
    } catch (error) {
      console.error("Search API failed:", error);
      setOptions([]);
    } finally {
      setSearching(false);
    }
  };


  const fetchTeamProductivity = async (agreementId) => {
    try {
      const payload = { document_id: agreementId };
      const res = await axiosInstance.post(`/chart/team-productvity/`, payload);
      const data = res.data.data;
      const id = res.data.agreement_id;
      setTeamProductivityDataId(id); // Store the agreement ID
      const newData = normalizeTeamProductivityData(data)
      setTeamProductivityData(newData);
    } catch (err) {
      console.error("Failed to fetch agreement productivity:", err);
    }
  };

  const values = teamProductivityData.map(item => parseFloat(item.days.toFixed(2)));
  const labels = teamProductivityData.map(item => item.department_name);

  const teamProductivityDataColumn = {
    series: values,
    options: {
      chart: {
        type: 'donut',
        height: 350,
        toolbar: {
          show: true,
          tools: {
            download: true,
          },
          export: {
            csv: { filename: "Team-Productivity-Summary" },
            svg: { filename: "Team-Productivity-Chart" },
            png: { filename: "Team-Productivity-Chart" },
          },
        },
        events: {
          dataPointSelection: function (event, chartContext, config) {
            if (teamProductivityDataId) {
              setTimeout(() => navigate(`/admin/contracts/${teamProductivityDataId}`), 100);
            }
          }
        },
      },
      labels: labels,
      colors: tailwindColors.slice(0, labels.length),
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: 600,
          colors: ["#263238"],
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          },
        },
        formatter: function (val, opts) {
          try {
            const seriesVal = opts?.w?.config?.series?.[opts.seriesIndex];
            const ceilVal = Math.ceil(parseFloat(seriesVal ?? 0));
            return `${ceilVal} Days`;
          } catch (e) {
            return `${Math.ceil(parseFloat(val ?? 0))} Days`;
          }

        },
      },
      tooltip: {
        y: {
          formatter: function (val, opts) {
            return `${Math.ceil(parseFloat(val ?? 0))} Days`
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '13px',
      },
      plotOptions: {
        pie: {
          donut: {
            size: '40%', // ✅ SMALLER GAP in center
            labels: {
              show: false,
            },
          },
        },
      },
      title: {
        text: 'Team Productivity Summary',
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#263238',
        },
      },
    }
  };


  const fetchPayemntFrequency = async (value) => {
    try {
      const response = await axiosInstance.post(`/chart/get/payment-frequency-value/`, {
        nature: value ? value : "all"
      });
      setPaymentFrequency(response.data.data);
    } catch (error) { }
  };

  const paymentFrequencyName =
    productWiseSummary &&
    paymentFrequency
      .filter((item) => item && item.payment_frequency)
      .map((item) => item.payment_frequency);

  const paymentValue =
    productWiseSummary &&
    paymentFrequency
      .filter((item) => item && item.payment_frequency)
      .map((item) => item.total_agreement_value);

  const maxpaymentValue = Math.max(...paymentValue);

  const yaxisPayemntOptions = {
    max: maxpaymentValue < 50000 ? 100000 : maxpaymentValue,
    title: {
      text: `${currency
        ? currency
        : ""
        }`,
    },
  };

  const payemntFrequencyData = {
    series: [
      {
        name: "Summary",
        data: paymentValue,
      },
    ],
    options: {
      chart: {
        type: "bar",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1000,
          animateGradually: {
            enabled: true,
            delay: 200,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 800,
          },
        },
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
          },
          export: {
            csv: {
              filename: "Summary-of-Payment-Type", // ✅ CSV file name
            },
            svg: {
              filename: "Chart-of-Payment-Type", // ✅ SVG file name
            },
            png: {
              filename: "Chart-of-Payment-Type", // ✅ PNG file name
            },
          },
        },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const id = paymentFrequency[config.dataPointIndex]?.contract_identifier;
            const payment_type = paymentFrequency[config.dataPointIndex]?.payment_frequency;
            navigate(`/admin/contracts?id=${id}`, {
              state: { payment_type, status: 'EXECUTED' }
            })
          },
        },
      },
      title: {
        text: "Summary  of Payment Type",
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#263238'
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "bold",
          colors: ["#263238"],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          opacity: 0.2,
        },
        formatter: function (val) {
          if (val >= 10000000) return Math.floor(val / 10000000) + ' Cr';
          if (val >= 100000) return Math.floor(val / 100000) + ' L';
          if (val >= 1000) return Math.floor(val / 1000) + ' K';
          return val;
        }
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      plotOptions: {
        bar: {
          distributed: true, // this line is mandatory
          horizontal: false,
          endingShape: "rounded",
          columnWidth: "80%",
        },
      },
      colors: tailwindColors.slice(0, paymentValue.length),
      xaxis: {
        categories: paymentFrequencyName,
      },

      tooltip: {
        y: {
          formatter: function (val) {
            return (
              `${currency
                ? currency + " "
                : ""
              }` + val
            );
          },
        },
      },
      yaxis: {
        ...yaxisPayemntOptions,
        min: 0,
        max: Math.ceil(Math.max(...paymentValue) * 1.25),
        tickAmount: 5,
        labels: {
          formatter: function (val) {
            if (val >= 10000000) return Math.floor(val / 10000000) + ' Cr';
            if (val >= 100000) return Math.floor(val / 100000) + ' L';
            if (val >= 1000) return Math.floor(val / 1000) + ' K';
            return val;
          }
        }
      }
    },
  };


  useEffect(() => {
    fetchProductWiseSummary();
    fetchTop10Customers();
    fetchPayemntFrequency();
    fetchAgreementCount();
    fetchTeamProductivity("")
  }, []);

  const onChangeNatureOfPayment = (val => {
    fetchPayemntFrequency(val)
  })
  const handleSelectChange = (value) => {
    fetchTop10Customers(value);
  };
  const customerNature = [
    { value: "customer", label: "Customer" },
    { value: "vendor", label: "Vendor" },
  ];
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const handleRangePickerChange = (dates, dateStrings) => {
    setStartDate(dateStrings[0]);
    setEndDate(dateStrings[1]);
  };
  useEffect(() => {
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
      fetchAgreementCount(formattedStartDate, formattedEndDate);
    }
  }, [startDate, endDate]);

  const disabledDate = (current) => {
    const today = dayjs().startOf("day");
    const oneYearAgo = dayjs().subtract(1, "year").startOf("day");
    return current && (current > today || current < oneYearAgo);
  };

  const sortedCustomers = [...top10Customers].sort((a, b) =>
    parseInt(b.total_agreement_value) - parseInt(a.total_agreement_value)
  );

  const valueToRankMap = {};
  let rank = 1;

  sortedCustomers.forEach((item, index) => {
    const val = parseInt(item.total_agreement_value);
    if (!valueToRankMap[val]) {
      valueToRankMap[val] = rank;
      rank++;
    }
  });

  const rankToFillMap = {};
  for (let r = 1; r <= 10; r++) {
    rankToFillMap[r] = 110 - r * 10;
  }

  const rawData = {
    sohan: {
      fresh: 1,
      under_negotiation: 1,
      executed: 0,
      under_execution: 4,
    },
    rinku: {
      fresh: 4,
      under_negotiation: 0,
      executed: 3,
      under_execution: 3,
    },
    shivani: {
      fresh: 3,
      under_negotiation: 2,
      executed: 0,
      under_execution: 2,
    },
  };

  const radarStatusLabels = ["Fresh", "Under Negotiation", "Under Execution", "Executed", "Transaction Closed"];
  const statuses = ["fresh", "under_negotiation", "under_execution", "executed", "transaction_closed"];

  const radarSeries = Object.entries(rawData).map(([user, statusCounts]) => ({
    name: user,
    data: statuses.map(status => statusCounts[status] || 0),
  }));

  const radarChartOptions = {
    chart: {
      type: "radar",
      height: 500,
      width: "100%",
      toolbar: {
        show: true,
        tools: {
          download: true,
        },
      },
      events: {
        markerClick: (event, chartContext, { seriesIndex }) => {
          setActiveUserIndex(seriesIndex);
        },
        dataPointMouseEnter: (event, chartContext, config) => {
          handlePointMouseEnter(config.seriesIndex);
        },
        dataPointMouseLeave: () => {
          handleMouseLeave();
        },
      },
      sparkline: { enabled: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 700,
      },
    },
    title: {
      text: "User-wise Agreement Status",
      align: "center",
      style: {
        fontSize: "18px",
        fontWeight: 600,
        color: "#1e293b",
      },
    },
    xaxis: {
      categories: radarStatusLabels,
      labels: {
        style: {
          fontSize: "14px",
          fontWeight: 500,
          colors: "#334155",
        },
      },

    },
    yaxis: {
      show: true,
      tickAmount: 4,
      min: 0,
      labels: {
        formatter: (val) => Math.round(val),
        style: {
          fontSize: "11px",
          colors: "#64748b",
        },
      },
    },
    stroke: {
      width: 2,
      curve: 'smooth'
    },
    fill: {
      opacity: 0.3,
    },
    markers: {
      size: Array.isArray(radarSeries)
        ? radarSeries.map(val => val === 0 ? 0 : 4)
        : 4,
      colors: ['#fff'],
      strokeColors: tailwindColors,
      strokeWidth: 2,
      hover: {
        size: 8
      }
    },
    colors: tailwindColors,
    legend: {
      show: true,
      position: "right",
      fontSize: "13px",
      fontWeight: 500,
      formatter: (seriesName, opts) => {
        const val = opts.w.globals.series[opts.seriesIndex];
        return val === 0 ? '' : `${seriesName}`;
      },
      onItemClick: { toggleDataSeries: true },
      onItemHover: {
        highlightDataSeries: true,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const labels = w.config.xaxis.categories;
        const value = series[seriesIndex][dataPointIndex];
        const user = labels[dataPointIndex]; // company ya user name
        const status = w.config.series[seriesIndex].name;

        if (value === 0) return "";

        // Tailwind se color match karke de sakta hai (ya manually bhi assign kar sakta)
        const statusColors = {
          Contracts: '#99f6e4', // Tailwind's teal-200
          Signed: '#c084fc', // example
          Expired: '#fca5a5', // example
        };

        const circleColor = statusColors[status] || '#d1d5db';
        return `
      <div style="font-size:13px; line-height:1.6; width:180px;">
        <div style="font-weight: 600;padding:8px8px8px0px; margin-bottom: 6px; background-color: #d1d5db;>${user}</div>
        <div style="display: flex; align-items: center; padding:8px8px8px0px ,gap: 8px;">
          <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${circleColor};"></span>
          <span> ${status}: <strong>${value}</strong> </span>
        </div>
      </div>
    `;
      },
    },

    grid: {
      padding: {
        top: -10,
        bottom: -10,
      },
    },
    dataLabels: {
      enabled: false,
    },
  };
  return (
    <>
      <div className="flex flex-row"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 -m-6">
        {/* Left charts */}
        <div className="col-span-2 grid grid-cols-2 gap-4">
          <div className="bg-white rounded-lg">
            <Select
              showSearch
              placeholder="Search Agreement..."
              optionFilterProp="label"
              filterOption={false}
              loading={searching}
              allowClear
              onClear={() => fetchTeamProductivity("")}
              onSearch={handleSearchTeamProductivity}
              onSelect={(value) => fetchTeamProductivity(value)}
              className="w-1/2 rounded-md mt-2 ml-2"
              size="middle"
              options={options}

            />

            {values.every(v => v === 0) ? (
              <div className="flex items-center justify-center h-[350px] text-[#263238] text-lg font-semibold rounded-b-lg">
                🚫 No Productivity Data Available
              </div>
            ) : (
              <ChartComponent
                chartData={{
                  options: teamProductivityDataColumn.options,
                  series: values,
                  additionalProps: { type: "donut", height: 350 },
                  rounded: "rounded-lg",
                }}
              />
            )}
          </div>
          <ChartComponent
            chartData={{
              options: productWiseData.options,
              series: productWiseData.series,
              additionalProps: { type: "bar", height: 300 },
              rounded: "rounded-lg",
            }}
          />
          {/* Chart 3 */}
          <div className="">
            <div className="bg-white rounded-t-lg">
              <span>
                <Select
                  defaultValue={{ value: "all", label: "All" }}
                  options={NatureOfPaymentType}
                  onChange={onChangeNatureOfPayment}
                  size="medium"
                  style={{
                    width: "14em",
                    marginLeft: "9px",
                    marginTop: "8px"
                  }}
                  placeholder={"Select Nature"}
                  allowClear
                />
              </span>
            </div>
            <ChartComponent
              chartData={{
                options: payemntFrequencyData.options,
                series: payemntFrequencyData.series,
                additionalProps: { type: "bar", height: 300 },
                rounded: "rounded-b-lg",
              }}
            />
          </div>
          {/* Chart 4 */}
          <div className="bg-white rounded-lg flex flex-col h-full">
            <div className="">
              <RangePicker
                onChange={handleRangePickerChange}
                disabledDate={disabledDate}
                style={{ width: "220px" }}
                className="ml-2 mt-2"
              />
            </div>
            <div className="flex-grow">
              {
                statusCount && statusCount.some(val => val > 0) ? (
                  <ChartComponent
                    chartData={{
                      options: agreementCountData.options,
                      series: agreementCountData.series,
                      additionalProps: { type: "pie", height: 350 },
                      rounded: "rounded-b-lg",
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-[350px] text-[#263238] text-lg font-semibold rounded-b-lg">
                    🚫 No Status Found
                  </div>
                )
              }
            </div>
          </div>
          {/* <div className="bg-white rounded-lg col-span-2 relative h-[470px] overflow-hidden">
            <ChartComponent
              chartData={{
                options: radarChartOptions,
                series: radarSeries,
                additionalProps: { type: "radar", height: 600, width: "100%" },
                rounded: "rounded-lg",
              }}
            />

            {activeUserIndex !== null && radarSeries[activeUserIndex] && (
              <div className="p-4 bg-gray-50 rounded-xl shadow-md ml-6 w-60 absolute bottom-0 right-0">
                <h3 className="font-semibold text-lg mb-2">
                  {radarSeries[activeUserIndex].name}
                </h3>
                <ul className="text-sm space-y-1">
                  {radarSeries[activeUserIndex].data.map((value, i) => (
                    <li key={i}>
                      <strong>{radarStatusLabels[i]}</strong>: {value}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div> */}
        </div>

        {/* Right Sidebar */}
        <div className="col-span-1 bg-white p-6 rounded-lg shadow-lg">
          <div className="bg-white rounded-t-lg mb-6 flex flex-row justify-between items-center">
            <h1
              className="font-semibold"
              style={{ fontSize: 18, paddingTop: "1%" }}
            >
              Top 10
            </h1>
            <span>
              <Select
                defaultValue={{ value: "customer", label: "Customer" }}
                options={customerNature}
                onChange={handleSelectChange}
                size="medium"
                style={{
                  width: "14em",
                  marginLeft: "9px",
                }}
                placeholder={"Select Nature"}
                allowClear
              />
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {top10Customers &&
              top10Customers.map((value, index) => {
                const val = parseInt(value.total_agreement_value);
                const rank = valueToRankMap[val];
                const completed = rankToFillMap[rank];
                const bgcolor = tailwindColors[index % tailwindColors.length];
                return (
                  <ProgressBar
                    key={index}
                    bgcolor={bgcolor}
                    completed={completed}
                    name={value.entity_name}
                    amount={value.total_agreement_value}
                    srNo={index + 1}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </>
  );
}

export default CompanyAnalytics;
